'use client';

import React, { useEffect, useRef } from 'react';

const tiers = [
  {
    name: 'Platinum',
    role: 'Title Sponsor',
    price: '₹15 Lakh',
    badge: 'Title Sponsor',
    badgeClass: 'tier-badge-exclusive',
    borderClass: 'border-primary border-2',
    highlight: false,
    passes: '30 Gala Passes + 10 Platinum Memberships',
    exclusivity: 'Category exclusive',
    benefits: [
      '"Prathaa 2026 presented by [Brand]" title',
      'Logo: Backdrop, stage, banners, invites - most prominent',
      'Title branding at Gala Evening with Javed Ali',
      'On-stage mention by anchor · Multiple, daily PA mentions',
      'Premium stall + entrance standee on ground',
      'Dedicated social media posts + story mentions',
      'Press release co-branding',
      '30 Complimentary Gala passes + 10 Platinum memberships',
      'Category exclusivity - sole sponsor in your category',
      'Dedicated page in Souvenir Programme',
      '30-second brand film screened at Gala Evening',
    ],
  },
  {
    name: 'Gold',
    role: 'Category Sponsor',
    price: '₹10 Lakh',
    badge: 'Category Sponsor',
    badgeClass: 'tier-badge-popular',
    borderClass: 'border-accent border-2',
    highlight: true,
    passes: '20 Gala Passes + 6 Gold Memberships',
    exclusivity: 'Shared category',
    benefits: [
      '"Gold Sponsor" branding across all materials',
      'Logo on backdrop & banners',
      'Logo on Gala Evening backdrop',
      'Daily PA mention · Mention at Sindoor Khela',
      'Standee on ground',
      'Shared social media post mentions',
      '20 Complimentary Gala passes + 6 Gold memberships',
      'Half-page in Souvenir Programme',
      'Feature in press & social media coverage',
    ],
  },
  {
    name: 'Silver',
    role: 'Supporting Sponsor',
    price: '₹5 Lakh',
    badge: null,
    badgeClass: '',
    borderClass: 'border-border',
    highlight: false,
    passes: '10 Gala Passes + 4 Silver Memberships',
    exclusivity: 'Shared category',
    benefits: [
      '"Silver Sponsor" acknowledgment on all materials',
      'Logo on banner listing',
      'Logo credit at Gala Evening',
      'Mention at Sindoor Khela',
      'Shared-zone standee on ground',
      'Group mention in social media',
      '10 Complimentary Gala passes + 4 Silver memberships',
      'Quarter-page in Souvenir Programme',
    ],
  },
];

export default function SponsorshipTiers() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('active'); }); },
      { threshold: 0.06 }
    );
    sectionRef?.current?.querySelectorAll('.reveal')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-muted/40 w-full px-4 md:px-12 pt-14 md:pt-20 pb-12 md:pb-16">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-14 reveal">
          <span className="inline-block px-4 py-1.5 rounded-full border border-border text-muted-foreground text-xs font-bold tracking-widest uppercase mb-4 md:mb-5">
            Sponsorship Packages
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black text-foreground tracking-tight mb-3 md:mb-4">
            Three Tiers. <span className="text-primary">One Stage.</span>
          </h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto">
            Every tier includes title branding at the Gala Evening with Javed Ali, logo placement across all materials, and complimentary passes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {tiers?.map((tier, i) => (
            <div
              key={tier?.name}
              className={`reveal bg-card border rounded-3xl md:rounded-4xl p-6 md:p-8 flex flex-col relative overflow-hidden transition-all duration-300 hover:shadow-gold hover:-translate-y-1 ${tier?.borderClass} ${tier?.highlight ? 'shadow-gold-lg' : ''}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {tier?.highlight && (
                <div className="absolute inset-0 bg-gradient-to-b from-accent/8 to-transparent pointer-events-none rounded-4xl" />
              )}
              {tier?.badge && (
                <span className={`absolute top-5 right-5 md:top-6 md:right-6 ${tier?.badgeClass}`}>{tier?.badge}</span>
              )}
              <div className="mb-1">
                <h3 className="font-display text-xl md:text-2xl font-black text-foreground tracking-tight">{tier?.name}</h3>
                <p className="text-muted-foreground text-xs font-semibold tracking-wide mt-0.5">{tier?.role}</p>
              </div>
              <div className="mb-2">
                <span className="font-display text-3xl md:text-4xl font-black text-primary">{tier?.price}</span>
              </div>
              <div className="mb-4 md:mb-5 flex flex-col gap-1.5">
                <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                  <svg className="text-accent" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                  {tier?.passes}
                </span>
                <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                  <svg className="text-accent" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                  {tier?.exclusivity}
                </span>
              </div>
              <ul className="space-y-2 flex-1 mb-5 md:mb-7">
                {tier?.benefits?.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-card-foreground/80">
                    <svg className="text-accent flex-shrink-0 mt-0.5" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5"/>
                    </svg>
                    {b}
                  </li>
                ))}
              </ul>
              <a
                href="#inquiry"
                className={`block text-center py-3 md:py-3.5 px-6 rounded-full font-bold text-sm transition-all duration-200 ${
                  tier?.highlight
                    ? 'bg-accent text-accent-foreground hover:bg-secondary hover:text-primary-foreground shadow-gold'
                    : 'border-2 border-foreground/20 text-foreground hover:border-primary hover:text-primary'
                }`}
              >
                Enquire About {tier?.name}
              </a>
            </div>
          ))}
        </div>

        <div className="mt-8 md:mt-10 text-center reveal">
          <p className="text-muted-foreground text-sm">
            Custom packages available. Contact{' '}
            <a href="mailto:hello@hoyichoyianondo.com" className="text-primary font-semibold hover:underline">hello@hoyichoyianondo.com</a>
          </p>
        </div>
      </div>
    </section>
  );
}